import { Component, StrictMode } from 'react';
import { Button, Card } from '@arkestro/arkestro-design-system';
import { LeftCircleOutlined, RightCircleOutlined, RightOutlined } from '@ant-design/icons';
import withTranslation from 'next-translate/withTranslation';
import { withRouter } from 'next/router';
import { UserContext } from '@providers/UserProvider/UserProvider';
import { PALM, CREATE_REQUESTS_GRAPHIC, COLLABORATE_REQUESTS_GRAPHIC, AWARD_GRAPHIC,
  SUPPLIERS_GRAPHIC, EVENTS_GRAPHIC, SMALL_OVAL, SMALL_RECTANGLE } from '@utils/images';
import { alertMessage } from 'utils/common';
import { setOnBoardStep, updateUserProfileFields } from 'api/UserApi';
import {
  getSpendCategories,
  getSpendCategoriesForCurrentUser,
  updateSpendCategoriesForCurrentUser
} from 'api/SpendCategoryApi';
import { FULL_LOGO_BLACK_URL } from 'utils/constants';
import SpendTypesTooltipLine from './SpendTypesTooltipLine';

// Caller passes `t` so the alt text localises alongside the step copy below
// — same shape as `steps`, `otherOverviewStep`, and `overviewSteps` lower in the file.
const graphics = t => ({
  awardGraphic: <img alt={t('buyer_onboarding.alt_award_illustration')} className='award-onboarding' src={AWARD_GRAPHIC} />,
  collaborateGraphic: <img alt={t('buyer_onboarding.alt_collaborate_illustration')} className='collaborate-onboarding' src={COLLABORATE_REQUESTS_GRAPHIC} />,
  createRequestsGraphic: <img alt={t('buyer_onboarding.alt_create_requests_illustration')} className='requests-onboarding' src={CREATE_REQUESTS_GRAPHIC} />,
  eventsGraphic: <img alt={t('buyer_onboarding.alt_events_illustration')} className='events-onboarding' src={EVENTS_GRAPHIC} />,
  logo: <img alt={t('general.logo')} className='logo_onboarding' src={FULL_LOGO_BLACK_URL} />,
  oval: <img alt={t('buyer_onboarding.alt_small_oval')} className='oval-onboarding' src={SMALL_OVAL} />,
  palm: <img alt={t('buyer_onboarding.alt_palm_illustration')} className='palm-onboarding' src={PALM} />,
  rectangle: <img alt={t('buyer_onboarding.alt_small_rectangle')} className='rectangle-onboarding' src={SMALL_RECTANGLE} />,
  suppliersGraphic: <img alt={t('buyer_onboarding.alt_suppliers_illustration')} className='supplier-onboarding' src={SUPPLIERS_GRAPHIC} />,
});

// Backend `category_type` values (bidops-app's SpendCategory enum). These are machine
// identifiers used only for API-value comparison below — not UI copy. The user-visible
// labels are rendered from `t('general.direct_spend')` / `t('general.indirect_spend')`.
const DIRECT_SPEND = 'Direct Spend';
const INDIRECT_SPEND = 'Indirect Spend';

const SPEND_CATEGORY_LIST_STYLE = { padding: 20 };

const steps = t => ([
  {
    buttonText: t('general.continue'),
    call: 'buyer_step_one',
    insetPageClassName: 'tall-info-section',
    position: 'first',
    privacyTermsSkipClassName: 'privacy-terms-line',
    skip: '',
    step: 'intro',
    text: '',
    title: t('onboarding.buyer.welcome_title')
  },
  {
    buttonText: t('general.continue'),
    call: 'start_overview',
    customTextClass: 'lets-begin-text',
    graphic: graphics(t).palm,
    insetPageClassName: 'short-info-section',
    privacyTermsSkipClassName: 'privacy-terms-line',
    skip: 'buyer_step_two',
    step: 'buyer_step_one',
    text: t('onboarding.buyer.what_you_can_do'),
    title: t('onboarding.welcome_to_arkestro')
  },
  {
    buttonText: t('general.continue'),
    call: 'completed',
    customTextClass: 'spend-selection-subtitle',
    insetPageClassName: 'longer-info-section',
    privacyTermsSkipClassName: 'privacy-terms-line',
    skip: 'completed',
    step: 'buyer_step_two',
    text: t('onboarding.buyer.what_spend_types'),
    title: t('onboarding.buyer.ready_to_start')
  }
]);

const overviewSteps = t => ([
  {
    graphic: graphics(t).createRequestsGraphic,
    insetPageClassName: 'wider-info-section',
    privacyTermsSkipClassName: 'privacy-terms-line',
    skip: 'buyer_step_two',
    step_position: 0,
    text: t('onboarding.buyer.features_description'),
    title: t('onboarding.buyer.manage_requests')
  },
  {
    graphic: graphics(t).collaborateGraphic,
    insetPageClassName: 'wider-info-section',
    privacyTermsSkipClassName: 'privacy-terms-line',
    skip: 'buyer_step_two',
    step_position: 1,
    text: t('onboarding.buyer.messaging_description'),
    title: t('onboarding.buyer.collaboration_title')
  },
  {
    graphic: graphics(t).suppliersGraphic,
    insetPageClassName: 'wider-info-section',
    privacyTermsSkipClassName: 'privacy-terms-line',
    skip: 'buyer_step_two',
    step_position: 2,
    text: t('onboarding.buyer.find_suppliers'),
    title: t('onboarding.buyer.manage_suppliers')
  },
  {
    graphic: graphics(t).eventsGraphic,
    insetPageClassName: 'wider-info-section',
    privacyTermsSkipClassName: 'privacy-terms-line',
    skip: 'buyer_step_two',
    step_position: 3,
    text: t('onboarding.buyer.invite_suppliers'),
    title: t('onboarding.buyer.run_live_events')
  },
  {
    buttonText: t('general.continue'),
    call: 'buyer_step_two',
    graphic: graphics(t).awardGraphic,
    insetPageClassName: 'wider-info-section',
    privacyTermsSkipClassName: 'privacy-terms-line',
    skip: 'buyer_step_two',
    step_position: 4,
    text: t('onboarding.buyer.analytics_description'),
    title: t('onboarding.buyer.awarding_title')
  }
]);

const getAsyncSpendCategories = async () => {
  const result = await getSpendCategories();

  if (result.success) return result.spendCategories;
  return undefined;
};

const getAsyncUserSpendCategories = async () => {
  const result = await getSpendCategoriesForCurrentUser();

  if (result.success) return result.spendCategories;
  return undefined;
};

const getAsyncCategoriesChecked = async () => {
  const spendCatList = await getAsyncSpendCategories();
  const userSpendCatList = await getAsyncUserSpendCategories();

  const categorySpendDisplayList = spendCatList.map(category => {
    const categoryInUserList = userSpendCatList.find(
      userCat => category.name === userCat.name && category.category_type === userCat.category_type
    );

    const catExistsInUser = categoryInUserList !== undefined;

    return {
      category_type: category.category_type,
      checked: catExistsInUser,
      id: category.id,
      name: category.name
    };
  });

  return categorySpendDisplayList;
};

const shouldUpdateCompanyName = user => user.is_self_serve && user.company_name === user.email;

class BuyerOnBoard extends Component {
  constructor (props) {
    super(props);

    const step = props.user.on_boarding || 'intro';

    this.t = props.i18n.t;

    this.state = {
      buyer_company_name: shouldUpdateCompanyName(props.user) ? '' : props.user.company_name,
      buyer_first_name: props.user.first_name,
      buyer_job_title: props.user.job_title,
      buyer_last_name: props.user.last_name,
      categorySpendDisplayList: [],
      initialLoad: true,
      overviewStep: -1,
      progress: false,
      step
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.buildInsetPage = this.buildInsetPage.bind(this);
    this.addFirstLastJobTitle = this.addFirstLastJobTitle.bind(this);
    this.buildSkipButton = this.buildSkipButton.bind(this);
    this.handleNavigation = this.handleNavigation.bind(this);
    this.buildOverviewPage = this.buildOverviewPage.bind(this);
    this.handleSetSpendCategoryChecked = this.handleSetSpendCategoryChecked.bind(this);
    this.handlePersistCategorySelections = this.handlePersistCategorySelections.bind(this);
    this.validateJobTitlePage = this.validateJobTitlePage.bind(this);
  }

  componentDidMount () {
    const { initialLoad } = this.state;

    if (initialLoad) {
      const categorySpendDisplayListCall = getAsyncCategoriesChecked();

      categorySpendDisplayListCall.then(result => {
        this.setState({ categorySpendDisplayList: result, initialLoad: false });
      });
    }
  }

  componentDidUpdate (prevProps) {
    if (prevProps.user?.id !== this.props.user?.id) {
      this.setState({
        buyer_company_name: shouldUpdateCompanyName(this.props.user) ? '' : this.props.user.company_name,
        buyer_first_name: this.props.user.first_name,
        buyer_job_title: this.props.user.job_title,
        buyer_last_name: this.props.user.last_name
      });
    }
  }

  handleSpecificNavigation (index) {
    this.setState({ overviewStep: index });
  }

  handlePersistCategorySelections () {
    const { categorySpendDisplayList } = this.state;

    updateSpendCategoriesForCurrentUser(categorySpendDisplayList);
  }

  handleSetSpendCategoryChecked (spendCategory) {
    const { categorySpendDisplayList } = this.state;

    const index = categorySpendDisplayList.map(e => e.id).indexOf(spendCategory.id);

    categorySpendDisplayList[index] = { ...spendCategory, checked: !spendCategory.checked };
    this.setState({ categorySpendDisplayList });
  }

  handleSubmit (currentStep, nextStep) {
    if (currentStep === 'intro') {
      const params = {
        first_name: this.state.buyer_first_name,
        job_title: this.state.buyer_job_title,
        last_name: this.state.buyer_last_name
      };

      if (shouldUpdateCompanyName(this.props.user)) params.company_name = this.state.buyer_company_name;
      this.updateUserProfileFields(params, nextStep);
      return;
    }

    if (currentStep === 'buyer_step_one') {
      if (nextStep === 'start_overview') {
        this.handleNavigation('forward');
      } else {
        this.updateOnboardStep(nextStep);
      }
      return;
    }
    if (currentStep === 'buyer_step_two') {
      this.handlePersistCategorySelections();
      this.updateOnboardStep(nextStep);
      return;
    }
    this.updateOnboardStep(nextStep);
    // step two operation, gathers some data?
  }

  handleChange (event) {
    this.setState({ [event.target.name]: event.target.value });
  }

  handleNavigation (direction) {
    let { overviewStep } = this.state;

    if (direction === 'forward') {
      overviewStep += 1;
    } else {
      overviewStep -= 1;
    }

    if (overviewStep > 4) {
      overviewStep = 4;
    } else if (overviewStep < 0) {
      overviewStep = 0;
    }

    this.setState({ overviewStep });
  }

  updateUserProfileFields (data, nextStep) {
    this.setState({ progress: true });
    updateUserProfileFields(data)
      .then(({ success, errors }) => {
        if (success) {
          this.updateOnboardStep(nextStep);
          return;
        }
        alertMessage(errors[0], 'error', 10);
        this.setState({ progress: false });
      });
  }

  updateOnboardStep (nextStep) {
    this.setState({ progress: true });
    setOnBoardStep(nextStep)
      .then(({ success, errors, step }) => {
        if (!success) {
          alertMessage(errors[0], 'error', 10);
          this.setState({ progress: false });
          return;
        }
        if (step !== 'completed') {
          this.setState({ progress: false, step });
          return;
        }
        if (!this.props.user.is_self_serve) {
          this.props.router.reload();
          return;
        }
        const introRequestId = this.props.user.intro_bid_request_id || '';

        if (!introRequestId) {
          this.props.router.reload();
          return;
        }
        this.props.router.push(`/bid_requests/${introRequestId}/dashboard?show_intercom_tour=true`);
        this.setState({ progress: false, step });
      });
  }

  addFirstLastJobTitle () {
    const firstName = this.state.buyer_first_name || '';
    const lastName = this.state.buyer_last_name || '';
    const jobTitle = this.state.buyer_job_title || '';
    const companyName = this.state.buyer_company_name || '';

    return (
      <div className='first-last-job-form'>
        <section className='name-section'>
          <div className='name-box'>
            <label className='body-text-form-label' htmlFor='buyer_first_name'>
              {this.t('general.first_name')}
              <input
                className='input-box-only-underline'
                id='buyer_first_name'
                name='buyer_first_name'
                onChange={this.handleChange}
                type='text'
                value={firstName}
              />
            </label>
          </div>
          <div className='name-box'>
            <label className='body-text-form-label' htmlFor='buyer_last_name'>
              {this.t('general.last_name')}
              <input
                className='input-box-only-underline'
                id='buyer_last_name'
                name='buyer_last_name'
                onChange={this.handleChange}
                type='text'
                value={lastName}
              />
            </label>
          </div>
        </section>
        <section className='job-title-section'>
          <div className='job-title-box'>
            <label className='body-text-form-label' htmlFor='buyer_job_title'>
              {this.t('general.job_title')}
              <input
                className='input-box-only-underline'
                id='buyer_job_title'
                name='buyer_job_title'
                onChange={this.handleChange}
                type='text'
                value={jobTitle}
              />
            </label>
          </div>

          {shouldUpdateCompanyName(this.props.user) && (
            <div className='job-title-box'>
              <label className='body-text-form-label' htmlFor='buyer_company_name'>
                {this.t('general.company_name')}
                <input
                  className='input-box-only-underline'
                  id='buyer_company_name'
                  name='buyer_company_name'
                  onChange={this.handleChange}
                  type='text'
                  value={companyName}
                />
              </label>
            </div>
          )}
        </section>
      </div>
    );
  }

  buildSkipButton (skipToStep) {
    return (
      <span className='skip-span'>
        <Button
          disabled={this.state.progress}
          loading={this.state.progress}
          onClick={() => this.handleSubmit(this.state.step, skipToStep)}
          size="small"
          type="text"
        >
          {this.t('general.skip')}
          <RightOutlined />
        </Button>
      </span>
    );
  }

  buildPrivacyTermsLine (stepDetails) {
    return (
      <div className={stepDetails.privacyTermsSkipClassName}>
        <span>
          <a
            className='Privacy-Policy-Ter'
            href='https://arkestro.com/privacy-policy'
            rel='noopener noreferrer'
            target='_blank'
          >
            {this.t('general.privacy_policy')}
          </a>
          &nbsp;|&nbsp;
          <a
            className='Privacy-Policy-Ter'
            href='https://arkestro.com/terms-of-use'
            rel='noopener noreferrer'
            target='_blank'
          >
            {this.t('general.terms_of_use')}
          </a>
        </span>
        {stepDetails.skip && this.buildSkipButton(stepDetails.skip)}
      </div>
    );
  }

  addSpendButtons () {
    const { categorySpendDisplayList } = this.state;

    const directSpend = categorySpendDisplayList.find(c => c.category_type === DIRECT_SPEND && c.name === 'None');
    const indirectSpend = categorySpendDisplayList.find(c => c.category_type === INDIRECT_SPEND && c.name === 'None');
    const directSpendSelected = directSpend && directSpend.checked;
    const indirectSpendSelected = indirectSpend && indirectSpend.checked;

    const other = categorySpendDisplayList.find(c => c.category_type === 'Other');
    const otherSelected = other && other.checked;

    return (
      <div style={SPEND_CATEGORY_LIST_STYLE}>
        <div className='dropdown-button-div'>
          <Button
            block
            ghost={!directSpendSelected}
            onClick={() => this.handleSetSpendCategoryChecked(directSpend)}
            type="secondary"
          >
            {this.t('general.direct_spend')}
          </Button>
        </div>
        <div className='dropdown-button-div'>
          <Button
            block
            ghost={!indirectSpendSelected}
            onClick={() => this.handleSetSpendCategoryChecked(indirectSpend)}
            type="secondary"
          >
            {this.t('general.indirect_spend')}
          </Button>
        </div>
        <div className='dropdown-button-div'>
          <Button
            block
            ghost={!otherSelected}
            onClick={() => this.handleSetSpendCategoryChecked(other)}
            type="secondary"
          >
            {this.t('general.other')}
          </Button>
        </div>

        <SpendTypesTooltipLine />
      </div>
    );
  }

  validateJobTitlePage () {
    /* eslint-disable camelcase */
    const { buyer_first_name, buyer_last_name, buyer_job_title, buyer_company_name } = this.state;

    let allFieldsAreFilledOut =
      buyer_first_name &&
      buyer_first_name.length > 0 &&
      buyer_last_name &&
      buyer_last_name.length > 0 &&
      buyer_job_title &&
      buyer_job_title.length > 0;

    if (shouldUpdateCompanyName(this.props.user))
      allFieldsAreFilledOut = allFieldsAreFilledOut && buyer_company_name && buyer_company_name.length > 0;
    /* eslint-enable camelcase */

    return allFieldsAreFilledOut;
  }

  buildInsetPage (stepDetails) {
    const shouldDisableSubmit = stepDetails.step === 'intro' && !this.validateJobTitlePage();

    return (
      <div className='flex-vertical-card'>
        <div className='title-text'>
          {stepDetails.title}&nbsp;{stepDetails.graphic}
        </div>
        <div className='body-text'>
          <div className={stepDetails.customTextClass ? stepDetails.customTextClass : ''}>{stepDetails.text}</div>
          {stepDetails.step === 'intro' && this.addFirstLastJobTitle()}
          {stepDetails.step === 'buyer_step_two' && this.addSpendButtons()}
          <div className='continue-button-div'>
            <Button
              disabled={this.state.progress || shouldDisableSubmit}
              loading={this.state.progress}
              onClick={() => this.handleSubmit(stepDetails.step, stepDetails.call)}
              type="secondary"
            >
              {stepDetails.buttonText}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  generateCircleButtons (numOfPages, minusIndex) {
    const pages = [];

    for (let index = 0; index < numOfPages; index += 1) {
      const graphic = index === minusIndex ? graphics(this.t).rectangle : graphics(this.t).oval;

      pages.push(
        <Button
          key={index}
          onClick={() => this.handleSpecificNavigation(index)}
          type='link'
        >
          {graphic}
        </Button>
      );
    }
    return pages;
  }

  buildOverviewPage (overviewStepDetails) {
    const endPageIndex = overviewSteps(this.t).length - 1;

    return (
      <div className='flex-horizontal-card'>
        <div className='text-column'>
          <div className='text-box'>
            <div className='title-text'>{overviewStepDetails.title}</div>
            <div className='body-text'>{overviewStepDetails.text}</div>
          </div>
          <div className='nav-box'>
            <Button
              disabled={this.state.progress || overviewStepDetails.step_position === 0}
              icon={<LeftCircleOutlined />}
              onClick={() => this.handleNavigation('backward')}
              type='link'
            />
            <span className='nav-circle-span'>
              {this.generateCircleButtons(overviewSteps(this.t).length, overviewStepDetails.step_position)}
            </span>
            <Button
              disabled={this.state.progress || overviewStepDetails.step_position === endPageIndex}
              icon={<RightCircleOutlined />}
              onClick={() => this.handleNavigation('forward')}
              type='link'
            />
          </div>
        </div>
        <div className='graphic-column'>
          <div className='graphic-div-area'>{overviewStepDetails.graphic}</div>
          {overviewStepDetails.step_position === endPageIndex && (
            <div className='continue-button-div'>
              <Button
                disabled={this.state.progress}
                loading={this.state.progress}
                onClick={() => this.handleSubmit(this.state.step, overviewStepDetails.call)}
                type="secondary"
              >
                {overviewStepDetails.buttonText}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  render () {
    const stepIndex = steps(this.t).map(st => st.step).indexOf(this.state.step);
    const [stepDetails, overview] =
      this.state.step === 'buyer_step_one' && this.state.overviewStep >= 0
        ? [overviewSteps(this.t)[this.state.overviewStep], true]
        : [steps(this.t)[stepIndex], false];
    const insetPage = !overview;

    return (
      <StrictMode>
        <Card className='buyer-onboard'>
          <div className={stepDetails.insetPageClassName}>
            <div className='header'>{graphics(this.t).logo}</div>
            {insetPage && this.buildInsetPage(stepDetails)}
            {overview && this.buildOverviewPage(stepDetails)}
            {this.buildPrivacyTermsLine(stepDetails)}
          </div>
        </Card>
      </StrictMode>
    );
  }
}

const exportedComponent = props => <UserContext.Consumer>
  {user => <BuyerOnBoard {...props} user={user} />}
</UserContext.Consumer>;

export default withRouter(withTranslation(exportedComponent, 'common'));
